import {
  Divider,
  HStack,
  PlacementWithLogical,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  VStack,
} from "@chakra-ui/react";
import React, { memo, PropsWithChildren, useCallback, useMemo } from "react";

import { useAuth } from "modules/providers/auth-provider";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import colors from "app/colors";
import { VerticalEllipsisIcon, ReplyIcon } from "modules/icons";
import { MessageFragment, MessageThreadFragment } from "gql/graphql";
import useRemovedFromThread from "modules/hooks/use-removed-from-thread";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import MessagePopoverDeleteMessageButton from "./message-popover-delete-message-button";
import MessagePopoverForwardMessageButton from "./message-popover-forward-message-button";
import MessagePopoverGenerateTaskButton from "./message-popover-generate-task-button";
import MessagePopoverToggleTranslationButton from "./message-popover-toggle-translation-button";
import MessagePopoverCopyMessageButton from "./message-popover-copy-message-button";
import MessagePopoverEditMessageButton from "./message-popover-edit-message-button";
import MessagePopoverMessageInfoButton from "./message-popover-message-info-button";
import MessagePopoverQuickRepliesButtons from "./message-popover-quick-replies-buttons";
import MessagePopoverReactionButton from "./message-popover-reaction-button";
import { MESSAGE_STATE, REACTION_TYPES } from "modules/utils/Constants";
import { isMessageThreadReadOnlyAndIsReader } from "modules/utils/Functions";
import HappycoMessagePopoverCreateWorkOrderButton from "modules/external/happyco/happyco-message-popover-create-work-order-button";
import HappycoMessagePopoverExportToExistingWorkOrderButton from "modules/external/happyco/happyco-message-popover-export-to-existing-work-order-button";
import { useMessageHover } from "modules/providers/message-hover-provider";
import { usePubSub } from "modules/providers/pub-sub-provider";

interface IProps extends PropsWithChildren {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
  placement?: PlacementWithLogical;
  placementOffset?: [number, number];
  messageState: MESSAGE_STATE;
}

const MessagePopover: React.FC<IProps> = ({
  message,
  messageThread,
  placement = "bottom-end",
  placementOffset = [5, -20],
  messageState,
  children,
}) => {
  const { me } = useAuth();
  const { isMeRemoved } = useRemovedFromThread(messageThread);
  const { publish } = usePubSub();

  const {
    isMessageMenuOpen,
    onMessageEnter,
    onMessageLeave,
    isSubMenuOpen,
    onSubmenuOpen,
    onSubmenuClose,
  } = useMessageHover();

  const _isOpen = useMemo(() => {
    if (messageState !== "sent") return false;
    if (message.deleted) return false;
    return isMessageMenuOpen(message.id);
  }, [isMessageMenuOpen, message.deleted, message.id, messageState]);

  const _isSubMenuOpen = useMemo(
    () => isSubMenuOpen(message.id, "more"),
    [isSubMenuOpen, message.id],
  );

  const _isReplySubMenuOpen = useMemo(
    () => isSubMenuOpen(message.id, "reply"),
    [isSubMenuOpen, message.id],
  );

  const { setQuotedMessages } = useMessageThreadDraft({
    draftId: messageThread.id,
  });

  const onReplyToMessageClick = useCallback(() => {
    setQuotedMessages(message);
    publish("focus-quill-editor", messageThread.id);
  }, [setQuotedMessages, message, publish, messageThread.id]);

  if (!me) return null;

  if (isMeRemoved) {
    return (
      <Popover
        trigger="hover"
        placement={placement}
        offset={placementOffset}
        closeDelay={200}
        returnFocusOnClose={false}
        isOpen={_isOpen}
        onOpen={onMessageEnter(message.id)}
        onClose={onMessageLeave(message.id)}
        flip
        isLazy
      >
        <PopoverTrigger>{children}</PopoverTrigger>
        <PopoverContent w="100%" p={2}>
          <VStack spacing={2} w="272px" h="full" alignItems="flex-start" p={2}>
            <MessagePopoverCopyMessageButton
              messageThread={messageThread}
              message={message}
            />
            <MessagePopoverToggleTranslationButton
              messageThread={messageThread}
              message={message}
            />
            <MessagePopoverForwardMessageButton
              message={message}
              messageThread={messageThread}
              displayText="Forward"
            />
            <MessagePopoverGenerateTaskButton
              message={message}
              messageThread={messageThread}
              displayText="Create Task from Message"
            />
            <MessagePopoverMessageInfoButton
              message={message}
              displayText="Message Info"
            />
          </VStack>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover
      trigger="hover"
      placement={placement}
      offset={placementOffset}
      returnFocusOnClose={false}
      openDelay={200}
      isOpen={_isOpen}
      onOpen={onMessageEnter(message.id)}
      onClose={onMessageLeave(message.id)}
      flip
      isLazy
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent w="full" h={9}>
        <HStack spacing={2} h="full" px={2}>
          {REACTION_TYPES.map((reaction) => (
            <MessagePopoverReactionButton
              key={reaction}
              message={message}
              type={reaction}
            />
          ))}
          <Divider
            orientation="vertical"
            h={5}
            borderColor={colors.grayPrimary}
            opacity={1}
          />
          {!isMessageThreadReadOnlyAndIsReader(messageThread, me) && (
            <Popover
              trigger="hover"
              placement="top-start"
              isOpen={_isReplySubMenuOpen}
              onOpen={onSubmenuOpen(message.id, "reply")}
              onClose={onSubmenuClose(message.id, "reply")}
            >
              <PopoverTrigger>
                <CircularIconButton
                  size="xs"
                  aria-label="Reply to Message"
                  icon={<ReplyIcon boxSize={5} fill={colors.blue} />}
                  onClick={onReplyToMessageClick}
                />
              </PopoverTrigger>
              <Portal>
                <PopoverContent w="100%">
                  <MessagePopoverQuickRepliesButtons
                    messageThread={messageThread}
                    message={message}
                  />
                </PopoverContent>
              </Portal>
            </Popover>
          )}
          <MessagePopoverForwardMessageButton
            message={message}
            messageThread={messageThread}
          />
          <MessagePopoverGenerateTaskButton
            message={message}
            messageThread={messageThread}
          />
          <MessagePopoverMessageInfoButton message={message} />
          <Popover
            placement="top-end"
            trigger="hover"
            offset={[9, 8]}
            isOpen={_isSubMenuOpen}
            onOpen={onSubmenuOpen(message.id, "more")}
            onClose={onSubmenuClose(message.id, "more")}
          >
            <PopoverTrigger>
              <CircularIconButton
                size="xs"
                aria-label="More Options"
                icon={
                  <SyncoTooltip
                    label="More"
                    openDelay={1000}
                    placement="top-end"
                  >
                    <VerticalEllipsisIcon boxSize={5} fill={colors.blue} />
                  </SyncoTooltip>
                }
              />
            </PopoverTrigger>
            <Portal>
              <PopoverContent w="fit-content" p={2}>
                <VStack w="full" alignItems="flex-start" spacing={3} p={2}>
                  <MessagePopoverEditMessageButton message={message} />
                  <MessagePopoverCopyMessageButton
                    messageThread={messageThread}
                    message={message}
                  />
                  <MessagePopoverToggleTranslationButton
                    messageThread={messageThread}
                    message={message}
                  />
                  <HappycoMessagePopoverCreateWorkOrderButton
                    messageThread={messageThread}
                    message={message}
                  />
                  <HappycoMessagePopoverExportToExistingWorkOrderButton
                    messageThread={messageThread}
                    message={message}
                  />
                  <MessagePopoverDeleteMessageButton message={message} />
                </VStack>
              </PopoverContent>
            </Portal>
          </Popover>
        </HStack>
      </PopoverContent>
    </Popover>
  );
};

export default memo(MessagePopover);
